import { Box, Heading, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { API_ROUTERS } from '../../../../data';
import { useRequest } from '../../../../hooks';
import { formValidateByRes } from '../../../../services';
import {
  IMemberToUser,
  MEMBER_TO_USER_DEFAULT_VALUES,
  MEMBER_TO_USER_VALIDATION,
} from '../../../../utils/forms/MemberToUser';
import { FormFooter } from '../../../Form/FormFooter';
import { FormInput } from '../../../Form/FormInput';

export const SendInvitation = ({ goBack, isCompleted }: { goBack: VoidFunction; isCompleted: VoidFunction }) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { memberId } = state;
  const { authRequest } = useRequest();
  const [isUserCanJoin, setIsUserCanJoin] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    setError,
    formState: { isValid },
  } = useForm<IMemberToUser>({
    mode: 'onChange',
    defaultValues: MEMBER_TO_USER_DEFAULT_VALUES,
    resolver: yupResolver(MEMBER_TO_USER_VALIDATION),
  });

  const onSubmit = async (data: IMemberToUser) => {
    try {
      await authRequest({ method: 'post', url: API_ROUTERS.MEMBER_TO_USER_SEND_INVITE(memberId), data });
      isCompleted();
    } catch (e: any) {
      const formErrors = formValidateByRes(e, setError);
      if (!formErrors && e?.data?.[0]) {
        setError('email', { message: e.data[0] });
      }
    }
  };

  useEffect(() => {
    const checkJoinState = async () => {
      try {
        const { data } = await authRequest(
          { method: 'post', url: API_ROUTERS.MEMBER_TO_USER_CHECK(memberId) },
          { hideErrorMessage: true },
        );
        setIsUserCanJoin(typeof data?.status === 'boolean' ? data?.status : true);
      } catch (e) {
        setIsUserCanJoin(true);
      }
    };

    checkJoinState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  return (
    <>
      <Heading fontSize='22px' fontWeight={500} color='brand.cyan'>
        {t('member.invite.heading')}
      </Heading>

      <Text mt={4}>{t('member.invite.text')}</Text>

      <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete='off'>
        <Box mt={6}>
          <FormInput
            name='email'
            label={`${t('member.invite.email.filed.label')}`}
            control={control}
            isReadOnly={!isUserCanJoin}
          />
        </Box>

        <FormFooter
          mt={10}
          submitBtn={{ title: t('member.invite.submit.btn'), isDisabled: !isValid || !isUserCanJoin }}
          cancelBtn={{ onClick: goBack }}
          tooltip={{ label: `${t('member.invite.submit.btn.tooltip')}`, isDisabled: isUserCanJoin }}
        />
      </form>
    </>
  );
};
